/**
 * Environment Variable Validation
 * ================================
 * Validates required environment variables at application startup
 */

interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

// Required environment variables for each environment
const REQUIRED_VARS = {
  development: [
    // API configuration is optional in dev (defaults to localhost)
  ],
  staging: [
    // Staging may not use proxy, so API URLs might be needed
    // But making them optional since we have fallback logic
  ],
  production: [
    // API and WS URLs are optional - have fallback logic in config/index.ts
    // VITE_API_BASE_URL - falls back to empty string (uses Vercel proxy)
    // VITE_WS_URL - falls back to wss://${window.location.host}
    // Supabase is optional until we have it fully configured
    // 'VITE_SUPABASE_URL',
    // 'VITE_SUPABASE_PUBLISHABLE_KEY',
  ],
};

// Optional but recommended variables
const RECOMMENDED_VARS = [
  'VITE_SENTRY_DSN',
  'VITE_ENV',
  'VITE_ENABLE_ANALYTICS',
];

/**
 * Validates that a URL is properly formatted
 */
const validateUrl = (url: string, field: string): ValidationError | null => {
  if (!url) {
    return { field, message: `${field} is required but not set` };
  }
  
  // Allow relative URLs in production
  if (url === '' || url === '/') {
    return null;
  }
  
  try {
    new URL(url);
    return null;
  } catch {
    // Check if it's a valid relative URL
    if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
      return null;
    }
    return { field, message: `${field} is not a valid URL: ${url}` };
  }
};

/**
 * Validates WebSocket URL
 */
const validateWebSocketUrl = (url: string, field: string): ValidationError | null => {
  if (!url) {
    return { field, message: `${field} is required but not set` };
  }
  
  // Allow relative URLs in production
  if (url === '' || url === '/') {
    return null;
  }
  
  if (!url.startsWith('ws://') && !url.startsWith('wss://')) {
    return { field, message: `${field} must start with ws:// or wss://: ${url}` };
  }
  
  return null;
};

/**
 * Validates boolean environment variables
 */
const validateBoolean = (value: string | undefined, field: string): ValidationError | null => {
  if (value === undefined) {
    return null; // Optional
  }
  
  const validValues = ['true', 'false', '1', '0', ''];
  if (!validValues.includes(value.toLowerCase())) {
    return { 
      field, 
      message: `${field} must be 'true', 'false', '1', or '0'. Got: ${value}` 
    };
  }
  
  return null;
};

/**
 * Validates Supabase configuration
 */
const validateSupabase = (): { errors: ValidationError[]; warnings: ValidationError[] } => {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 
              import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  // In production, Supabase is recommended but not required
  const isProduction = import.meta.env.MODE === 'production' || 
                      window.location.hostname.includes('aightbetsports.com');
  
  if (isProduction) {
    if (!url) {
      warnings.push({
        field: 'VITE_SUPABASE_URL',
        message: 'Supabase URL is recommended in production for auth features',
      });
    } else {
      const urlError = validateUrl(url, 'VITE_SUPABASE_URL');
      if (urlError) errors.push(urlError);
    }
    
    if (!key) {
      warnings.push({
        field: 'VITE_SUPABASE_PUBLISHABLE_KEY',
        message: 'Supabase public key is recommended in production for auth features',
      });
    }
  }
  
  return { errors, warnings };
};

/**
 * Main validation function
 */
export const validateEnvironment = (): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];
  
  // Determine environment
  const environment = import.meta.env.VITE_ENV || 
                     (import.meta.env.MODE === 'production' ? 'production' : 'development');
  
  // Check required variables based on environment
  const requiredVars = REQUIRED_VARS[environment as keyof typeof REQUIRED_VARS] || [];
  
  for (const varName of requiredVars) {
    const value = import.meta.env[varName];
    if (!value) {
      errors.push({
        field: varName,
        message: `Required environment variable ${varName} is not set`,
      });
    }
  }
  
  // Validate API URLs
  const apiUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL;
  if (apiUrl) {
    const apiError = validateUrl(apiUrl, 'VITE_API_BASE_URL');
    if (apiError) errors.push(apiError);
  }
  
  // Validate WebSocket URL
  const wsUrl = import.meta.env.VITE_WS_URL;
  if (wsUrl) {
    const wsError = validateWebSocketUrl(wsUrl, 'VITE_WS_URL');
    if (wsError) errors.push(wsError);
  }
  
  // Validate Supabase configuration
  const supabaseValidation = validateSupabase();
  errors.push(...supabaseValidation.errors);
  warnings.push(...supabaseValidation.warnings);
  
  // Validate boolean flags
  const booleanFlags = [
    'VITE_ENABLE_ANALYTICS',
    'VITE_ENABLE_LIVE_BETTING',
    'VITE_ENABLE_AI_PREDICTIONS',
    'VITE_ENABLE_WEBSOCKETS',
    'VITE_ENABLE_DEBUG_MODE',
  ];
  
  for (const flag of booleanFlags) {
    const value = import.meta.env[flag];
    const error = validateBoolean(value, flag);
    if (error) errors.push(error);
  }
  
  // Check recommended variables
  for (const varName of RECOMMENDED_VARS) {
    const value = import.meta.env[varName];
    if (!value && environment === 'production') {
      warnings.push({
        field: varName,
        message: `Recommended environment variable ${varName} is not set`,
      });
    }
  }
  
  // Warn about deprecated variables
  if (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_BASE_URL) {
    warnings.push({
      field: 'VITE_API_URL',
      message: 'VITE_API_URL is deprecated. Use VITE_API_BASE_URL instead',
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Formats validation results for display
 */
export const formatValidationResults = (result: ValidationResult): string => {
  const lines: string[] = [];
  
  if (result.errors.length > 0) {
    lines.push('❌ Environment Validation Failed:');
    lines.push('');
    lines.push('Errors:');
    for (const error of result.errors) {
      lines.push(`  • ${error.field}: ${error.message}`);
    }
  }
  
  if (result.warnings.length > 0) {
    if (lines.length > 0) lines.push('');
    lines.push('⚠️  Warnings:');
    for (const warning of result.warnings) {
      lines.push(`  • ${warning.field}: ${warning.message}`);
    }
  }
  
  if (result.isValid && result.warnings.length === 0) {
    lines.push('✅ Environment configuration is valid');
  }
  
  return lines.join('\n');
};

/**
 * Throws an error if environment validation fails
 */
export const validateEnvironmentOrThrow = (): void => {
  const result = validateEnvironment();
  
  if (!result.isValid) {
    const message = formatValidationResults(result);
    console.error(message);
    throw new Error('Environment validation failed. Check console for details.');
  }
  
  // Log warnings but don't throw
  if (result.warnings.length > 0) {
    console.warn(formatValidationResults(result));
  }
};

/**
 * Gets a safe version of environment variables for logging
 * (masks sensitive values)
 */
export const getSafeEnvironmentVariables = (): Record<string, string> => {
  const env = import.meta.env;
  const safe: Record<string, string> = {};
  
  const sensitiveKeys = [
    'VITE_SUPABASE_PUBLISHABLE_KEY',
    'VITE_SUPABASE_ANON_KEY',
    'VITE_JWT_SECRET_KEY',
    'VITE_ANTHROPIC_API_KEY',
    'VITE_OPENAI_API_KEY',
    'VITE_CFBD_API_KEY',
    'VITE_SENTRY_DSN',
  ];
  
  for (const [key, value] of Object.entries(env)) {
    if (typeof value !== 'string') continue;
    
    if (sensitiveKeys.some(sensitive => key.includes(sensitive))) {
      // Mask sensitive values
      safe[key] = value ? '***' + value.slice(-4) : 'not set';
    } else {
      safe[key] = value;
    }
  }
  
  return safe;
};

export default {
  validateEnvironment,
  validateEnvironmentOrThrow,
  formatValidationResults,
  getSafeEnvironmentVariables,
};